"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const jsx_runtime_1 = require("react/jsx-runtime");
const react_1 = require("react");
const labelStyle = {
    display: 'block',
    fontSize: 12,
    fontWeight: 700,
    color: '#666',
    textTransform: 'uppercase',
    letterSpacing: 0.3,
    marginBottom: 4,
};
const inputStyle = {
    width: '100%',
    padding: '10px 12px',
    border: '1px solid #ddd',
    borderRadius: 8,
    fontSize: 14,
    boxSizing: 'border-box',
};
const rowStyle = { display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 12 };
const halfStyle = { flex: '1 1 200px', minWidth: 0 };
/** Lead.address is free text OR a structured object, so render whichever we were given. */
const addressToText = (address) => {
    if (typeof address === 'string')
        return address;
    if (address && typeof address === 'object') {
        const a = address;
        return [a.addressLine1 ?? a.line1 ?? a.street, a.city ?? a.suburb, a.state, a.postalCode ?? a.postcode]
            .filter(Boolean)
            .join(', ');
    }
    return '';
};
function ClaimAccountPanel({ apiBaseUrl, businessId, previewKey, onClaimed }) {
    const [state, setState] = (0, react_1.useState)(null);
    const [loading, setLoading] = (0, react_1.useState)(true);
    const [open, setOpen] = (0, react_1.useState)(false);
    const [saving, setSaving] = (0, react_1.useState)(false);
    const [error, setError] = (0, react_1.useState)('');
    const [done, setDone] = (0, react_1.useState)(null);
    const [form, setForm] = (0, react_1.useState)({
        businessName: '',
        firstName: '',
        lastName: '',
        contactRole: '',
        email: '',
        phone: '',
        website: '',
        abn: '',
        address: '',
        password: '',
        confirmPassword: '',
    });
    const setField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));
    (0, react_1.useEffect)(() => {
        fetch(`${apiBaseUrl}/crm/presentation/${businessId}/claim`, { headers: { 'X-Presentation-Key': previewKey } })
            .then((res) => (res.ok ? res.json() : null))
            .then((payload) => {
            if (!payload)
                return;
            setState(payload);
            // Guarded: a response without prefill used to blank the whole Presentation page.
            const p = payload.prefill || {};
            setForm((prev) => ({
                ...prev,
                businessName: p.businessName || '',
                firstName: p.firstName || '',
                lastName: p.lastName || '',
                contactRole: p.contactRole || '',
                email: p.email || '',
                phone: p.phone || '',
                website: p.website || '',
                abn: p.abn || '',
                address: addressToText(p.address),
            }));
        })
            .catch(() => {
            /* The proposal itself must still render if this one panel cannot load. */
        })
            .finally(() => setLoading(false));
    }, [apiBaseUrl, businessId, previewKey]);
    const submit = async () => {
        setError('');
        if (form.password.length < 8) {
            setError('Please choose a password of at least 8 characters.');
            return;
        }
        // Checked here as well as on the server: a typo in a password you cannot see locks you out of
        // an account that can only be claimed once.
        if (form.password !== form.confirmPassword) {
            setError('Those two passwords do not match.');
            return;
        }
        setSaving(true);
        try {
            const res = await fetch(`${apiBaseUrl}/crm/presentation/${businessId}/claim`, {
                method: 'POST',
                headers: { 'X-Presentation-Key': previewKey, 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    password: form.password,
                    businessName: form.businessName,
                    firstName: form.firstName,
                    lastName: form.lastName,
                    contactRole: form.contactRole,
                    email: form.email,
                    phone: form.phone,
                    website: form.website,
                    abn: form.abn,
                    address: form.address,
                }),
            });
            const result = await res.json().catch(() => ({}));
            if (!res.ok) {
                setError(result.error || 'Could not create your login.');
                return;
            }
            setDone({ email: result.email, loginUrl: result.loginUrl });
            onClaimed?.();
        }
        catch {
            setError('Network error creating your login.');
        }
        finally {
            setSaving(false);
        }
    };
    if (loading || !state)
        return null;
    if (done) {
        return ((0, jsx_runtime_1.jsxs)("section", { style: { marginTop: 32, padding: 20, borderRadius: 12, background: '#ecfdf5', border: '1px solid #a7f3d0' }, children: [(0, jsx_runtime_1.jsx)("h3", { style: { margin: '0 0 8px', fontSize: 18, color: '#065f46' }, children: "\u2713 Your login is ready" }), (0, jsx_runtime_1.jsxs)("p", { style: { margin: '0 0 12px', fontSize: 14, color: '#065f46' }, children: ["Sign in as ", (0, jsx_runtime_1.jsx)("strong", { children: done.email }), " with the password you just chose."] }), (0, jsx_runtime_1.jsx)("a", { href: done.loginUrl, style: { display: 'inline-block', padding: '12px 20px', borderRadius: 8, background: '#065f46', color: 'white', fontWeight: 700, fontSize: 14, textDecoration: 'none' }, children: "Go to your dashboard" })] }));
    }
    if (state.claimedAt) {
        return ((0, jsx_runtime_1.jsx)("section", { style: { marginTop: 32, padding: 16, borderRadius: 10, background: '#f8f9fb', border: '1px solid #eee', fontSize: 14, color: '#444' }, children: "You\u2019ve already set up your login. Sign in with your email address, or use \u201Cforgot password\u201D if you need to reset it." }));
    }
    // Nothing provisioned yet - no account exists to claim, so say nothing rather than offering
    // something that cannot work.
    if (!state.claimable)
        return null;
    return ((0, jsx_runtime_1.jsxs)("section", { style: { marginTop: 32, padding: 20, borderRadius: 12, border: '2px solid #667eea', background: '#f8faff' }, children: [(0, jsx_runtime_1.jsx)("h3", { style: { margin: '0 0 6px', fontSize: 18 }, children: "Make this yours" }), (0, jsx_runtime_1.jsx)("p", { style: { margin: '0 0 14px', fontSize: 14, color: '#555' }, children: "Set a password and this demo becomes your account \u2014 the same site, the same dashboard, nothing rebuilt. We\u2019ve filled in everything you\u2019ve already told us, so just check it over." }), !open ? ((0, jsx_runtime_1.jsx)("button", { type: "button", onClick: () => setOpen(true), style: { padding: '14px 22px', borderRadius: 8, border: 'none', background: '#667eea', color: 'white', fontSize: 14, fontWeight: 700, cursor: 'pointer' }, children: "Create my login" })) : ((0, jsx_runtime_1.jsxs)(jsx_runtime_1.Fragment, { children: [(0, jsx_runtime_1.jsxs)("div", { style: rowStyle, children: [(0, jsx_runtime_1.jsxs)("div", { style: halfStyle, children: [(0, jsx_runtime_1.jsx)("label", { style: labelStyle, children: "First name" }), (0, jsx_runtime_1.jsx)("input", { type: "text", value: form.firstName, onChange: (e) => setField('firstName', e.target.value), style: inputStyle })] }), (0, jsx_runtime_1.jsxs)("div", { style: halfStyle, children: [(0, jsx_runtime_1.jsx)("label", { style: labelStyle, children: "Last name" }), (0, jsx_runtime_1.jsx)("input", { type: "text", value: form.lastName, onChange: (e) => setField('lastName', e.target.value), style: inputStyle })] })] }), (0, jsx_runtime_1.jsxs)("div", { style: rowStyle, children: [(0, jsx_runtime_1.jsxs)("div", { style: halfStyle, children: [(0, jsx_runtime_1.jsxs)("label", { style: labelStyle, children: ["Email ", state.prefill.needsEmail ? '' : '(your login)'] }), (0, jsx_runtime_1.jsx)("input", { type: "email", value: form.email, onChange: (e) => setField('email', e.target.value), style: inputStyle })] }), (0, jsx_runtime_1.jsxs)("div", { style: halfStyle, children: [(0, jsx_runtime_1.jsx)("label", { style: labelStyle, children: "Phone" }), (0, jsx_runtime_1.jsx)("input", { type: "tel", value: form.phone, onChange: (e) => setField('phone', e.target.value), style: inputStyle })] })] }), (0, jsx_runtime_1.jsxs)("div", { style: rowStyle, children: [(0, jsx_runtime_1.jsxs)("div", { style: halfStyle, children: [(0, jsx_runtime_1.jsx)("label", { style: labelStyle, children: "Business name" }), (0, jsx_runtime_1.jsx)("input", { type: "text", value: form.businessName, onChange: (e) => setField('businessName', e.target.value), style: inputStyle })] }), (0, jsx_runtime_1.jsxs)("div", { style: halfStyle, children: [(0, jsx_runtime_1.jsx)("label", { style: labelStyle, children: "Your role" }), (0, jsx_runtime_1.jsx)("input", { type: "text", value: form.contactRole, onChange: (e) => setField('contactRole', e.target.value), style: inputStyle })] })] }), (0, jsx_runtime_1.jsxs)("div", { style: rowStyle, children: [(0, jsx_runtime_1.jsxs)("div", { style: halfStyle, children: [(0, jsx_runtime_1.jsx)("label", { style: labelStyle, children: "Website" }), (0, jsx_runtime_1.jsx)("input", { type: "text", value: form.website, onChange: (e) => setField('website', e.target.value), style: inputStyle })] }), (0, jsx_runtime_1.jsxs)("div", { style: halfStyle, children: [(0, jsx_runtime_1.jsx)("label", { style: labelStyle, children: "ABN" }), (0, jsx_runtime_1.jsx)("input", { type: "text", value: form.abn, onChange: (e) => setField('abn', e.target.value), style: inputStyle })] })] }), (0, jsx_runtime_1.jsxs)("div", { style: { marginBottom: 12 }, children: [(0, jsx_runtime_1.jsx)("label", { style: labelStyle, children: "Address" }), (0, jsx_runtime_1.jsx)("input", { type: "text", value: form.address, onChange: (e) => setField('address', e.target.value), style: inputStyle })] }), (0, jsx_runtime_1.jsxs)("div", { style: { ...rowStyle, borderTop: '1px solid #e6e8f5', paddingTop: 14 }, children: [(0, jsx_runtime_1.jsxs)("div", { style: halfStyle, children: [(0, jsx_runtime_1.jsx)("label", { style: labelStyle, children: "Choose a password" }), (0, jsx_runtime_1.jsx)("input", { type: "password", value: form.password, onChange: (e) => setField('password', e.target.value), autoComplete: "new-password", placeholder: "At least 8 characters", style: inputStyle })] }), (0, jsx_runtime_1.jsxs)("div", { style: halfStyle, children: [(0, jsx_runtime_1.jsx)("label", { style: labelStyle, children: "Confirm password" }), (0, jsx_runtime_1.jsx)("input", { type: "password", value: form.confirmPassword, onChange: (e) => setField('confirmPassword', e.target.value), autoComplete: "new-password", onKeyDown: (e) => {
                                            if (e.key === 'Enter' && !saving)
                                                submit();
                                        }, style: inputStyle })] })] }), error && (0, jsx_runtime_1.jsx)("div", { style: { color: '#b91c1c', fontSize: 13, marginBottom: 12 }, children: error }), (0, jsx_runtime_1.jsx)("button", { type: "button", onClick: submit, disabled: saving, style: { padding: '14px 22px', borderRadius: 8, border: 'none', background: saving ? '#ccc' : '#667eea', color: 'white', fontSize: 14, fontWeight: 700, cursor: saving ? 'wait' : 'pointer' }, children: saving ? 'Creating…' : 'Create my login' })] }))] }));
}
exports.default = ClaimAccountPanel;
